import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import ParentForm from '../components/ParentForm';
import DocumentsPanel from '../components/DocumentsPanel';
import studentsService from '../services/studentsService';
import classesService from '../services/classesService';
import tracksService from '../services/tracksService';
import securityService from '../services/securityService';
import { ArrowLeft, Plus, Trash, Phone, Envelope, Briefcase, MapPin, Info, X, CircleNotch, PencilSimple, Warning } from '@phosphor-icons/react';
import DialogModal from '../components/DialogModal';
import LockedFeature from '../components/LockedFeature';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

// Validated values accepted by the custody-update endpoint (CustodyType enum).
const CUSTODY_TYPE_VALUES = [
  { value: 'legal_guardian', label: 'Legal Guardian' },
  { value: 'custodial', label: 'Custodial' },
  { value: 'non_custodial', label: 'Non-custodial' },
  { value: 'emergency_only', label: 'Emergency Only' },
];
// Same list, plus a blank "use the backend default" option — used when
// linking a parent, where omitting custody_type lets StudentParent's own
// "guardian" default apply rather than forcing a choice up front.
const CUSTODY_TYPE_OPTIONS = [{ value: '', label: 'Default (Guardian)' }, ...CUSTODY_TYPE_VALUES];

function custodyLabel(value) {
  const match = CUSTODY_TYPE_VALUES.find(o => o.value === value);
  return match ? match.label : 'Guardian';
}

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight
  );
}

// Freeform school-defined key/value fields on a student (e.g. bus route,
// uniform size) — see Student.custom_fields in the backend.
function CustomFieldsEditor({ value, onChange, disabled }) {
  const entries = Object.entries(value || {});

  const updateEntry = (index, key, val) => {
    const next = entries.map((entry, i) => (i === index ? [key, val] : entry));
    onChange(Object.fromEntries(next));
  };

  const removeEntry = (index) => {
    onChange(Object.fromEntries(entries.filter((_, i) => i !== index)));
  };

  const addEntry = () => {
    onChange({ ...(value || {}), '': '' });
  };

  return (
    <div className="form-group">
      <label>Custom Fields</label>
      {entries.map(([key, val], index) => (
        <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <input
            placeholder="Field name"
            value={key}
            onChange={(e) => updateEntry(index, e.target.value, val)}
            disabled={disabled}
            style={{ flex: 1 }}
          />
          <input
            placeholder="Value"
            value={val ?? ''}
            onChange={(e) => updateEntry(index, key, e.target.value)}
            disabled={disabled}
            style={{ flex: 1 }}
          />
          <button type="button" className="btn-icon" onClick={() => removeEntry(index)} disabled={disabled} title="Remove field">
            <Trash size={16} />
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-secondary" onClick={addEntry} disabled={disabled}>
        <Plus size={14} /> Add Field
      </button>
    </div>
  );
}

// Photo upload with an optional square crop before it's sent to the
// server — uploads immediately (separate endpoint from the rest of the
// student edit form) rather than waiting for "Save Changes".
function PhotoUploadField({ studentId, currentPhotoUrl, onUploaded, disabled }) {
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [fileMeta, setFileMeta] = useState({ name: 'photo.jpg', type: 'image/jpeg' });
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef(null);

  const onSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only JPEG or PNG images are allowed');
      return;
    }
    setFileMeta({ name: file.name, type: file.type });
    const reader = new FileReader();
    reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
    reader.readAsDataURL(file);
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  };

  const resetSelection = () => {
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  const handleUpload = async () => {
    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) {
      toast.error('Select and crop a photo first');
      return;
    }
    setUploading(true);
    try {
      const image = imgRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(completedCrop.width * scaleX));
      canvas.height = Math.max(1, Math.round(completedCrop.height * scaleY));
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0, 0,
        canvas.width, canvas.height
      );
      const blob = await new Promise(resolve => canvas.toBlob(resolve, fileMeta.type, 0.92));
      if (!blob) throw new Error('Failed to process image');
      const formData = new FormData();
      formData.append('file', blob, fileMeta.name);
      const result = await studentsService.uploadStudentPhoto(studentId, formData);
      toast.success('Photo uploaded');
      resetSelection();
      onUploaded(result.photo_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="form-group">
      <label>Photo</label>
      {currentPhotoUrl && !imgSrc && (
        <div style={{ marginBottom: 8 }}>
          <img src={currentPhotoUrl} alt="Current" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }} />
        </div>
      )}
      <input type="file" accept="image/jpeg,image/png" onChange={onSelectFile} disabled={disabled || uploading} />
      {imgSrc && (
        <div style={{ marginTop: 12 }}>
          <ReactCrop crop={crop} onChange={(_, percentCrop) => setCrop(percentCrop)} onComplete={(c) => setCompletedCrop(c)} aspect={1}>
            <img ref={imgRef} src={imgSrc} alt="Crop preview" onLoad={onImageLoad} style={{ maxWidth: '100%', maxHeight: 300 }} />
          </ReactCrop>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn btn-sm btn-secondary" onClick={resetSelection} disabled={uploading}>Cancel</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Simple modal for editing student
function EditStudentModal({ student, open, onClose, onSave, onPhotoUploaded }) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    class_id: '',
    nationality: '',
    religion: '',
    blood_group: '',
    medical_conditions: '',
    address: '',
    status: '',
    admission_date: '',
    admission_type: 'new',
    previous_school_name: '',
    transfer_certificate_number: '',
    custom_fields: {},
  });
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    if (student) {
      setForm({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        date_of_birth: student.date_of_birth || '',
        gender: student.gender || '',
        class_id: student.class_id || '',
        nationality: student.nationality || '',
        religion: student.religion || '',
        blood_group: student.blood_group || '',
        medical_conditions: student.medical_conditions || '',
        address: student.address || '',
        status: student.status || '',
        admission_date: student.admission_date || '',
        admission_type: student.admission_type || 'new',
        previous_school_name: student.previous_school_name || '',
        transfer_certificate_number: student.transfer_certificate_number || '',
        custom_fields: student.custom_fields || {},
      });
    }
  }, [student]);

  useEffect(() => {
    if (open) {
      const loadClasses = async () => {
        try {
          const data = await classesService.listClasses();
          const classList = Array.isArray(data) ? data : (data.items || []);
          setClasses(classList);
        } catch (err) {
          toast.error('Failed to load classes');
        }
      };
      loadClasses();
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Field changed: ${name} = ${value}`);
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Drop any custom-field rows still using a blank key before saving.
      const cleanedCustomFields = Object.fromEntries(
        Object.entries(form.custom_fields || {}).filter(([key]) => key.trim() !== '')
      );
      await onSave({ ...form, custom_fields: cleanedCustomFields });
    } catch (err) {
      console.error('Error in modal submit:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', paddingBottom: 0 }}>
        <div className="modal-header">
          <h2>Edit Student</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form" style={{ maxHeight: 'calc(90vh - 130px)', overflowY: 'auto' }}>
          <PhotoUploadField
            studentId={student?.id}
            currentPhotoUrl={student?.photo_url}
            onUploaded={(photoUrl) => onPhotoUploaded && onPhotoUploaded(photoUrl)}
            disabled={saving}
          />

          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input 
                name="first_name" 
                value={form.first_name} 
                onChange={handleChange}
                disabled={saving}
                required 
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input 
                name="last_name" 
                value={form.last_name} 
                onChange={handleChange}
                disabled={saving}
                required 
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date of Birth</label>
              <input 
                name="date_of_birth" 
                value={form.date_of_birth} 
                onChange={handleChange}
                disabled={saving}
                type="date"
              />
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select 
                name="gender" 
                value={form.gender} 
                onChange={handleChange}
                disabled={saving}
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Class</label>
              <select 
                name="class_id" 
                value={form.class_id} 
                onChange={handleChange}
                disabled={saving}
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                disabled={saving || student?.status === 'active'}
                required
              >
                <option value="">Select</option>
                <option value="active">Active</option>
                <option value="graduated">Graduated</option>
                <option value="transferred">Transferred</option>
                <option value="withdrawn">Withdrawn</option>
                <option value="expelled">Expelled</option>
                <option value="inactive">Inactive</option>
              </select>
              {student?.status === 'active' && (
                <small style={{ color: 'var(--text-secondary)' }}>Use the "Exit Student" action to end enrollment — it captures a reason and checks clearance.</small>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Admission Type</label>
              <select name="admission_type" value={form.admission_type} onChange={handleChange} disabled={saving}>
                <option value="new">New admission</option>
                <option value="transfer_in">Transfer in (from another school)</option>
              </select>
            </div>
          </div>

          {form.admission_type === 'transfer_in' && (
            <div className="form-row">
              <div className="form-group">
                <label>Previous School</label>
                <input name="previous_school_name" value={form.previous_school_name} onChange={handleChange} disabled={saving} />
              </div>
              <div className="form-group">
                <label>Transfer Certificate No.</label>
                <input name="transfer_certificate_number" value={form.transfer_certificate_number} onChange={handleChange} disabled={saving} />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Nationality</label>
              <input 
                name="nationality" 
                value={form.nationality} 
                onChange={handleChange}
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label>Religion</label>
              <input 
                name="religion" 
                value={form.religion} 
                onChange={handleChange}
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Blood Group</label>
              <input 
                name="blood_group" 
                value={form.blood_group} 
                onChange={handleChange}
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label>Admission Date</label>
              <input 
                name="admission_date" 
                value={form.admission_date} 
                onChange={handleChange}
                disabled={saving}
                type="date"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Medical Conditions</label>
            <input 
              name="medical_conditions" 
              value={form.medical_conditions} 
              onChange={handleChange}
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label>Address</label>
            <input
              name="address"
              value={form.address}
              onChange={handleChange}
              disabled={saving}
            />
          </div>

          <CustomFieldsEditor
            value={form.custom_fields}
            onChange={(custom_fields) => setForm(f => ({ ...f, custom_fields }))}
            disabled={saving}
          />

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (
                <span className="button-loading">
                  <CircleNotch size={16} weight="fill" className="spinner" />
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const CLEARANCE_LABELS = {
  fees: 'Fees',
  library: 'Library',
  hostel: 'Hostel',
  transport: 'Transport',
};

function clearanceDetail(key, item) {
  if (key === 'fees') return item.clear ? 'No outstanding balance' : `Outstanding balance: GHS ${item.outstanding_balance.toFixed(2)}`;
  if (key === 'library') return item.clear ? 'No active loans or fines' : `${item.active_loans} active loan(s), GHS ${item.unpaid_fines.toFixed(2)} in unpaid fines`;
  if (key === 'hostel') return item.clear ? 'No active allocation' : 'Has an active hostel allocation';
  if (key === 'transport') return item.clear ? 'No active enrollment' : 'Has an active transport enrollment';
  return '';
}

// Ends a student's time at the school — captures a reason, previews
// clearance across fees/library/hostel/transport, and records an audit row.
function ExitStudentModal({ student, open, onClose, onExited }) {
  const [form, setForm] = useState({ status: 'graduated', reason: '', exit_date: new Date().toISOString().split('T')[0], transfer_destination_school: '', confirm_incomplete_clearance: false });
  const [clearance, setClearance] = useState(null);
  const [loadingClearance, setLoadingClearance] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !student) return;
    setForm({ status: 'graduated', reason: '', exit_date: new Date().toISOString().split('T')[0], transfer_destination_school: '', confirm_incomplete_clearance: false });
    setLoadingClearance(true);
    studentsService.getExitClearance(student.id)
      .then(setClearance)
      .catch(() => toast.error('Failed to load exit clearance'))
      .finally(() => setLoadingClearance(false));
  }, [open, student]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.reason.trim()) {
      toast.error('A reason is required');
      return;
    }
    setSubmitting(true);
    try {
      await onExited(form);
    } finally {
      setSubmitting(false);
    }
  };

  const overallClear = clearance?.overall_clear;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>Exit Student</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <p style={{ margin: '0 0 16px' }}>
            End <strong>{student.first_name} {student.last_name}</strong>'s enrollment at this school.
          </p>

          <div className="form-group">
            <label>New Status</label>
            <select name="status" value={form.status} onChange={handleChange} disabled={submitting} required>
              <option value="graduated">Graduated</option>
              <option value="transferred">Transferred</option>
              <option value="withdrawn">Withdrawn</option>
              <option value="expelled">Expelled</option>
            </select>
          </div>

          {form.status === 'transferred' && (
            <div className="form-group">
              <label>Transferring To</label>
              <input name="transfer_destination_school" value={form.transfer_destination_school} onChange={handleChange} disabled={submitting} placeholder="Destination school name" />
            </div>
          )}

          <div className="form-group">
            <label>Exit Date</label>
            <input type="date" name="exit_date" value={form.exit_date} onChange={handleChange} disabled={submitting} required />
          </div>

          <div className="form-group">
            <label>Reason *</label>
            <textarea name="reason" value={form.reason} onChange={handleChange} disabled={submitting} required />
          </div>

          <div className="form-group">
            <label>Exit Clearance</label>
            {loadingClearance ? (
              <p style={{ color: 'var(--text-secondary)' }}>Checking clearance...</p>
            ) : clearance ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {Object.entries(CLEARANCE_LABELS).map(([key, label]) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 8px', borderRadius: 4, background: clearance[key].clear ? 'rgba(6,95,70,0.08)' : 'rgba(154,52,18,0.08)' }}>
                    <span>{label}</span>
                    <span style={{ color: clearance[key].clear ? '#065F46' : '#9A3412' }}>{clearanceDetail(key, clearance[key])}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: 'var(--text-secondary)' }}>Unable to load clearance status.</p>
            )}
          </div>

          {clearance && !overallClear && (
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <input type="checkbox" id="confirm-incomplete-clearance" checked={form.confirm_incomplete_clearance} onChange={e => setForm(f => ({ ...f, confirm_incomplete_clearance: e.target.checked }))} disabled={submitting} />
              <label htmlFor="confirm-incomplete-clearance" style={{ fontWeight: 'normal' }}>I acknowledge this student's clearance is incomplete and want to proceed anyway.</label>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting || loadingClearance || (clearance && !overallClear && !form.confirm_incomplete_clearance)}>
              {submitting ? 'Recording...' : 'Confirm Exit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Bring a formerly graduated/transferred/withdrawn/expelled student back to
// ACTIVE status in a (possibly new) class — distinct from Reactivate, which
// only ever reverses the temporary Inactive status.
function ReEnrollModal({ student, open, onClose, onReEnrolled }) {
  const [form, setForm] = useState({ class_id: '', reason: '' });
  const [classes, setClasses] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ class_id: '', reason: '' });
    classesService.listClasses()
      .then(data => setClasses(Array.isArray(data) ? data : (data.items || [])))
      .catch(() => toast.error('Failed to load classes'));
  }, [open]);

  if (!open || !student) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.class_id) {
      toast.error('Please select a class');
      return;
    }
    if (!form.reason.trim()) {
      toast.error('A reason is required');
      return;
    }
    setSubmitting(true);
    try {
      await onReEnrolled(form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>Re-enroll Student</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <p style={{ margin: '0 0 16px' }}>
            Bring <strong>{student.first_name} {student.last_name}</strong> back to active status.
          </p>

          <div className="form-group">
            <label>Class *</label>
            <select name="class_id" value={form.class_id} onChange={handleChange} disabled={submitting} required>
              <option value="">Select Class</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Reason *</label>
            <textarea name="reason" value={form.reason} onChange={handleChange} disabled={submitting} required />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Re-enrolling...' : 'Re-enroll'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const SIBLING_RELATIONSHIP_TYPES = ['sibling', 'step', 'half', 'twin'];

// Explicit sibling link for step/half-siblings who don't share a registered
// Parent record — siblings who DO share a parent already show up in the
// Siblings list without needing this.
function AddSiblingModal({ studentId, open, onClose, onAdded, excludeIds }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [relationshipType, setRelationshipType] = useState('sibling');
  const [addingId, setAddingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setResults([]);
    setRelationshipType('sibling');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!search.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      studentsService.listStudents({ search, limit: 20 })
        .then(data => setResults((data.items || []).filter(s => s.id !== studentId && !excludeIds.includes(s.id))))
        .catch(() => toast.error('Failed to search students'))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(handle);
  }, [search, open, studentId, excludeIds]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Link Sibling</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-content" style={{ padding: '20px' }}>
          <div className="form-group">
            <label>Relationship Type</label>
            <select value={relationshipType} onChange={e => setRelationshipType(e.target.value)}>
              {SIBLING_RELATIONSHIP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <input
            type="text"
            placeholder="Search by student name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '14px', margin: '12px 0' }}
          />
          {loading ? (
            <p style={{ color: 'var(--text-secondary)' }}>Searching...</p>
          ) : results.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>
              <p>{search.trim() ? 'No students match your search' : 'Start typing to search for a student'}</p>
            </div>
          ) : (
            <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
              {results.map(s => (
                <div
                  key={s.id}
                  style={{
                    padding: '12px', marginBottom: '8px', border: '1px solid var(--border-color)',
                    borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{s.full_name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      ID: {s.student_id} | Class: {s.class_name || '-'}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={async () => {
                      setAddingId(s.id);
                      try {
                        await onAdded(s.id, relationshipType);
                      } finally {
                        setAddingId(null);
                      }
                    }}
                    disabled={addingId === s.id}
                  >
                    {addingId === s.id ? 'Linking...' : 'Link'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// A dedicated emergency-contact record for a student — distinct from
// Parent.is_emergency_contact, since an emergency contact (a neighbor, a
// family friend) may not be a registered parent/guardian at all.
function EmergencyContactModal({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState({ name: '', relationship: '', phone: '', alternate_phone: '', priority_order: 1 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || '',
        relationship: initial?.relationship || '',
        phone: initial?.phone || '',
        alternate_phone: initial?.alternate_phone || '',
        priority_order: initial?.priority_order || 1,
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: name === 'priority_order' ? Number(value) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.relationship.trim() || !form.phone.trim()) {
      toast.error('Name, relationship, and phone are required');
      return;
    }
    setSubmitting(true);
    try {
      await onSave(form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>{initial ? 'Edit' : 'Add'} Emergency Contact</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input name="name" value={form.name} onChange={handleChange} disabled={submitting} required />
            </div>
            <div className="form-group">
              <label>Relationship *</label>
              <input name="relationship" value={form.relationship} onChange={handleChange} disabled={submitting} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Phone *</label>
              <input name="phone" value={form.phone} onChange={handleChange} disabled={submitting} required />
            </div>
            <div className="form-group">
              <label>Alternate Phone</label>
              <input name="alternate_phone" value={form.alternate_phone} onChange={handleChange} disabled={submitting} />
            </div>
          </div>
          <div className="form-group">
            <label>Priority Order</label>
            <input type="number" min="1" name="priority_order" value={form.priority_order} onChange={handleChange} disabled={submitting} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const TRANSFER_REQUEST_STATUSES = ['requested', 'records_prepared', 'released', 'acknowledged'];

// Records-release workflow trail around a student transferring out to
// another school. Deliberately additive/observational — does NOT change
// Student.status; that still only happens via the Exit Student action.
function TransferRequestModal({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState({ receiving_school_name: '', release_date: '', notes: '', status: 'requested', documents_released: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        receiving_school_name: initial?.receiving_school_name || '',
        release_date: initial?.release_date || '',
        notes: initial?.notes || '',
        status: initial?.status || 'requested',
        documents_released: initial?.documents_released || '',
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!initial && !form.receiving_school_name.trim()) {
      toast.error('Receiving school name is required');
      return;
    }
    setSubmitting(true);
    try {
      await onSave(form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>{initial ? 'Update' : 'New'} Transfer Request</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          {!initial && (
            <div className="form-group">
              <label>Receiving School *</label>
              <input name="receiving_school_name" value={form.receiving_school_name} onChange={handleChange} disabled={submitting} required />
            </div>
          )}
          {initial && (
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={form.status} onChange={handleChange} disabled={submitting}>
                {TRANSFER_REQUEST_STATUSES.map(s => (
                  <option key={s} value={s}>{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group">
            <label>Release Date</label>
            <input type="date" name="release_date" value={form.release_date} onChange={handleChange} disabled={submitting} />
          </div>
          {initial && (
            <div className="form-group">
              <label>Documents Released</label>
              <input name="documents_released" value={form.documents_released} onChange={handleChange} disabled={submitting} placeholder="Comma-separated" />
            </div>
          )}
          <div className="form-group">
            <label>Notes</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} disabled={submitting} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StudentDetailPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [parents, setParents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showParentForm, setShowParentForm] = useState(false);
  const [deletingParentId, setDeletingParentId] = useState(null);
  const [regeneratingStudent, setRegeneratingStudent] = useState(false);
  const [regeneratingParentId, setRegeneratingParentId] = useState(null);
  const [showLinkStudentDialog, setShowLinkStudentDialog] = useState(false);
  const [selectedParentForLink, setSelectedParentForLink] = useState(null);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [linkingStudentId, setLinkingStudentId] = useState(null);
  const [searchStudentFilter, setSearchStudentFilter] = useState('');
  const [confirmDeleteParentId, setConfirmDeleteParentId] = useState(null);
  const [confirmRegenerateStudent, setConfirmRegenerateStudent] = useState(false);
  const [confirmRegenerateParentId, setConfirmRegenerateParentId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [confirmReactivate, setConfirmReactivate] = useState(false);
  const [showReEnrollModal, setShowReEnrollModal] = useState(false);

  // Siblings
  const [siblings, setSiblings] = useState([]);
  const [showAddSiblingModal, setShowAddSiblingModal] = useState(false);
  const [confirmRemoveSiblingId, setConfirmRemoveSiblingId] = useState(null);

  // Emergency contacts
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [showEmergencyContactModal, setShowEmergencyContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [confirmRemoveContactId, setConfirmRemoveContactId] = useState(null);

  // Transfer requests
  const [transferRequests, setTransferRequests] = useState([]);
  const [showTransferRequestModal, setShowTransferRequestModal] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);

  // Custody type
  const [custodyUpdatingParentId, setCustodyUpdatingParentId] = useState(null);
  const [linkCustodyType, setLinkCustodyType] = useState('');

  // Court-order pickup restriction
  const [restrictionDialog, setRestrictionDialog] = useState(null); // { parentId, parentName, reason }
  const [savingRestriction, setSavingRestriction] = useState(false);

  // Track (elective / subject-track pathway) — current term's assignment
  const [studentTrack, setStudentTrack] = useState(null);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [selectedTrackToAssign, setSelectedTrackToAssign] = useState('');
  const [savingTrack, setSavingTrack] = useState(false);

  // Load student details and parents
    // Save handler for edit modal
    const handleEditStudent = async (formData) => {
      try {
        // Add student_id to the payload - backend requires it
        const payload = {
          ...formData,
          student_id: student.student_id
        };
        console.log('Updating student with payload:', payload);
        const response = await studentsService.updateStudent(studentId, payload);
        
        console.log('Student update response:', response);

        // Reload student data after successful update
        await loadStudentDetails();

        if (response?.waitlisted) {
          toast.info(`The selected class is full — student was added to the waitlist at position ${response.position}.`);
        } else {
          toast.success('Student updated successfully');
        }
        setShowEditModal(false);
      } catch (error) {
        console.error('Error updating student:', error);
        toast.error(error.response?.data?.detail || error.message || 'Failed to update student');
      }
    };
    const handleExitStudent = async (formData) => {
      try {
        await studentsService.exitStudent(studentId, formData);
        await loadStudentDetails();
        toast.success('Student exit recorded');
        setShowExitModal(false);
      } catch (error) {
        toast.error(error.message || 'Failed to record student exit');
      }
    };
    const handleDeactivateStudent = async (reason) => {
      try {
        await studentsService.deactivateStudent(studentId, reason);
        await loadStudentDetails();
        toast.success('Student deactivated');
      } catch (error) {
        toast.error(error.message || 'Failed to deactivate student');
      }
    };
    const handleReactivateStudent = async () => {
      try {
        await studentsService.reactivateStudent(studentId);
        await loadStudentDetails();
        toast.success('Student reactivated');
      } catch (error) {
        toast.error(error.message || 'Failed to reactivate student');
      }
    };
  const loadStudentDetails = useCallback(() => {
    setLoading(true);
    Promise.all([
      studentsService.getStudent(studentId),
      studentsService.getStudentParents(studentId),
      studentsService.getStudentEnrollments(studentId).catch(() => []),
      studentsService.getSiblings(studentId).catch(() => []),
      studentsService.getEmergencyContacts(studentId).catch(() => []),
      studentsService.getTransferRequests(studentId).catch(() => []),
      tracksService.getStudentTrack(studentId).catch(() => null),
    ])
      .then(([studentData, parentsData, enrollmentsData, siblingsData, contactsData, transfersData, trackData]) => {
        setStudent(studentData);
        setParents(parentsData || []);
        setEnrollments(enrollmentsData || []);
        setSiblings(siblingsData || []);
        setEmergencyContacts(contactsData || []);
        setTransferRequests(transfersData || []);
        setStudentTrack(trackData);
      })
      .catch(err => {
        toast.error(err.message || 'Failed to load student details');
        navigate('/students');
      })
      .finally(() => setLoading(false));
  }, [studentId, navigate]);

  useEffect(() => {
    loadStudentDetails();
  }, [loadStudentDetails]);

  useEffect(() => {
    if (studentTrack?.academic_term_id) {
      tracksService.list({ academic_term_id: studentTrack.academic_term_id })
        .then(setAvailableTracks)
        .catch(() => {});
    } else {
      setAvailableTracks([]);
    }
  }, [studentTrack?.academic_term_id]);

  const handleAssignTrack = async () => {
    if (!selectedTrackToAssign || !studentTrack?.academic_term_id) return;
    setSavingTrack(true);
    try {
      if (studentTrack.student_track_id) {
        await tracksService.removeStudentTrack(studentTrack.student_track_id);
      }
      await tracksService.enrollStudent(selectedTrackToAssign, studentId, studentTrack.academic_term_id);
      const refreshed = await tracksService.getStudentTrack(studentId);
      setStudentTrack(refreshed);
      setSelectedTrackToAssign('');
      toast.success('Track updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update track');
    } finally {
      setSavingTrack(false);
    }
  };

  const handleRemoveTrack = async () => {
    if (!studentTrack?.student_track_id) return;
    setSavingTrack(true);
    try {
      await tracksService.removeStudentTrack(studentTrack.student_track_id);
      const refreshed = await tracksService.getStudentTrack(studentId);
      setStudentTrack(refreshed);
      toast.success('Removed from track');
    } catch (error) {
      toast.error(error.message || 'Failed to remove from track');
    } finally {
      setSavingTrack(false);
    }
  };

  const handleDeleteParent = (parentId) => {
    setConfirmDeleteParentId(parentId);
  };

  const confirmDeleteParent = async () => {
    if (!confirmDeleteParentId) return;
    setConfirmLoading(true);
    try {
      setDeletingParentId(confirmDeleteParentId);
      await studentsService.removeParentFromStudent(studentId, confirmDeleteParentId);
      toast.success('Parent removed successfully');
      setParents(parents.filter(p => p.id !== confirmDeleteParentId));
    } catch (error) {
      toast.error(error.message || 'Failed to remove parent');
    } finally {
      setDeletingParentId(null);
      setConfirmLoading(false);
      setConfirmDeleteParentId(null);
    }
  };

  const handleParentAdded = () => {
    setShowParentForm(false);
    loadStudentDetails();
  };

  const handleRegenerateStudentCredentials = () => {
    setConfirmRegenerateStudent(true);
  };

  const confirmRegenerateStudentCredentials = async () => {
    setConfirmLoading(true);
    try {
      setRegeneratingStudent(true);
      const result = await studentsService.regenerateStudentCredentials(studentId);
      toast.success('Student portal credentials regenerated successfully');
      setStudent(prev => ({
        ...prev,
        portal_account: result.portal_account
      }));
    } catch (error) {
      toast.error(error.message || 'Failed to regenerate student credentials');
    } finally {
      setRegeneratingStudent(false);
      setConfirmLoading(false);
      setConfirmRegenerateStudent(false);
    }
  };

  const handleRegenerateParentCredentials = (parentId) => {
    setConfirmRegenerateParentId(parentId);
  };

  const confirmRegenerateParentCredentials = async () => {
    if (!confirmRegenerateParentId) return;
    setConfirmLoading(true);
    try {
      setRegeneratingParentId(confirmRegenerateParentId);
      const result = await studentsService.regenerateParentCredentials(studentId, confirmRegenerateParentId);
      toast.success('Parent portal credentials regenerated successfully');
      setParents(prev => prev.map(parent => 
        parent.id === confirmRegenerateParentId 
          ? { ...parent, portal_account: result.portal_account }
          : parent
      ));
    } catch (error) {
      toast.error(error.message || 'Failed to regenerate parent credentials');
    } finally {
      setRegeneratingParentId(null);
      setConfirmLoading(false);
      setConfirmRegenerateParentId(null);
    }
  };

  const handleOpenLinkStudentDialog = async (parentId) => {
    setSelectedParentForLink(parentId);
    setSearchStudentFilter('');
    setLinkCustodyType('');
    try {
      // Fetch all students from the school (limit 100 max)
      const allStudents = await studentsService.listStudents({ limit: 100 });
      // Filter out the current student
      const filtered = (allStudents.items || []).filter(s => s.id !== studentId);
      setAvailableStudents(filtered);
      setShowLinkStudentDialog(true);
    } catch (error) {
      toast.error('Failed to load students');
    }
  };

  const handleLinkParentToStudent = async (targetStudentId) => {
    if (!selectedParentForLink) return;

    try {
      setLinkingStudentId(targetStudentId);
      await studentsService.linkParentToStudent(targetStudentId, selectedParentForLink, linkCustodyType);
      toast.success('Parent linked to student successfully');
      setShowLinkStudentDialog(false);
      setSelectedParentForLink(null);
      setSearchStudentFilter('');
      setLinkCustodyType('');
    } catch (error) {
      toast.error(error.message || 'Failed to link parent to student');
    } finally {
      setLinkingStudentId(null);
    }
  };

  const handleUpdateCustodyType = async (parentId, custodyType) => {
    setCustodyUpdatingParentId(parentId);
    try {
      await studentsService.updateCustodyType(studentId, parentId, custodyType);
      toast.success('Custody type updated');
      setParents(prev => prev.map(p => (p.id === parentId ? { ...p, custody_type: custodyType } : p)));
    } catch (error) {
      toast.error(error.message || 'Failed to update custody type');
    } finally {
      setCustodyUpdatingParentId(null);
    }
  };

  const openRestrictionDialog = (parent) => {
    setRestrictionDialog({ parentId: parent.id, parentName: `${parent.first_name} ${parent.last_name}`, reason: parent.restriction_reason || '' });
  };

  const handleSetPickupRestriction = async (reason) => {
    if (!restrictionDialog) return;
    const parentId = restrictionDialog.parentId;
    setSavingRestriction(true);
    try {
      await securityService.setPickupRestriction(studentId, parentId, true, reason);
      toast.success('Pickup access restricted');
      setParents(prev => prev.map(p => (
        p.id === parentId ? { ...p, is_pickup_restricted: true, restriction_reason: reason } : p
      )));
      setRestrictionDialog(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to set pickup restriction');
    } finally {
      setSavingRestriction(false);
    }
  };

  const handleClearPickupRestriction = async (parentId) => {
    setSavingRestriction(true);
    try {
      await securityService.setPickupRestriction(studentId, parentId, false);
      toast.success('Pickup restriction lifted');
      setParents(prev => prev.map(p => (
        p.id === parentId ? { ...p, is_pickup_restricted: false, restriction_reason: null } : p
      )));
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to lift pickup restriction');
    } finally {
      setSavingRestriction(false);
    }
  };

  const handleReEnrollStudent = async (formData) => {
    try {
      await studentsService.reEnrollStudent(studentId, formData);
      await loadStudentDetails();
      toast.success('Student re-enrolled');
      setShowReEnrollModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to re-enroll student');
    }
  };

  const handlePhotoUploaded = (photoUrl) => {
    setStudent(prev => (prev ? { ...prev, photo_url: photoUrl } : prev));
  };

  const handleAddSibling = async (siblingStudentId, relationshipType) => {
    try {
      await studentsService.addSibling(studentId, { sibling_student_id: siblingStudentId, relationship_type: relationshipType });
      toast.success('Sibling linked successfully');
      setShowAddSiblingModal(false);
      await loadStudentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to link sibling');
    }
  };

  const confirmRemoveSibling = async () => {
    if (!confirmRemoveSiblingId) return;
    setConfirmLoading(true);
    try {
      await studentsService.removeSibling(studentId, confirmRemoveSiblingId);
      toast.success('Sibling link removed');
      setSiblings(prev => prev.filter(s => s.id !== confirmRemoveSiblingId));
    } catch (error) {
      toast.error(error.message || 'Failed to remove sibling link');
    } finally {
      setConfirmLoading(false);
      setConfirmRemoveSiblingId(null);
    }
  };

  const handleSaveEmergencyContact = async (formData) => {
    try {
      if (editingContact) {
        await studentsService.updateEmergencyContact(studentId, editingContact.id, formData);
        toast.success('Emergency contact updated');
      } else {
        await studentsService.addEmergencyContact(studentId, formData);
        toast.success('Emergency contact added');
      }
      setShowEmergencyContactModal(false);
      setEditingContact(null);
      await loadStudentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to save emergency contact');
    }
  };

  const confirmRemoveContact = async () => {
    if (!confirmRemoveContactId) return;
    setConfirmLoading(true);
    try {
      await studentsService.removeEmergencyContact(studentId, confirmRemoveContactId);
      toast.success('Emergency contact removed');
      setEmergencyContacts(prev => prev.filter(c => c.id !== confirmRemoveContactId));
    } catch (error) {
      toast.error(error.message || 'Failed to remove emergency contact');
    } finally {
      setConfirmLoading(false);
      setConfirmRemoveContactId(null);
    }
  };

  const handleSaveTransferRequest = async (formData) => {
    try {
      if (editingTransfer) {
        await studentsService.updateTransferRequest(studentId, editingTransfer.id, formData);
        toast.success('Transfer request updated');
      } else {
        await studentsService.createTransferRequest(studentId, formData);
        toast.success('Transfer request created');
      }
      setShowTransferRequestModal(false);
      setEditingTransfer(null);
      await loadStudentDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to save transfer request');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading student details...</div>
      </Layout>
    );
  }

  if (!student) {
    return (
      <Layout>
        <div className="empty-state">Student not found</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        {/* Header */}
        <div className="page-header">
          <div className="header-with-back">
            <button
              className="btn-icon"
              onClick={() => navigate('/students')}
              title="Go back"
            >
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div>
                <h1>{student.first_name} {student.last_name}</h1>
                <p>Student ID: {student.student_id}</p>
              </div>
              <button
                className="btn btn-primary"
                style={{ marginLeft: '16px', height: '36px' }}
                onClick={() => setShowEditModal(true)}
              >
                Edit Student
              </button>
              {student.status === 'active' && (
                <>
                  <button
                    className="btn btn-secondary"
                    style={{ height: '36px' }}
                    onClick={() => setShowDeactivateDialog(true)}
                  >
                    Deactivate
                  </button>
                  <button
                    className="btn btn-danger"
                    style={{ height: '36px' }}
                    onClick={() => setShowExitModal(true)}
                  >
                    Exit Student
                  </button>
                </>
              )}
              {student.status === 'inactive' && (
                <button
                  className="btn btn-primary"
                  style={{ height: '36px' }}
                  onClick={() => setConfirmReactivate(true)}
                >
                  Reactivate
                </button>
              )}
              {['graduated', 'transferred', 'withdrawn', 'expelled'].includes(student.status) && (
                <button
                  className="btn btn-primary"
                  style={{ height: '36px' }}
                  onClick={() => setShowReEnrollModal(true)}
                >
                  Re-enroll
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Student Info Cards */}
        <div className="student-info-grid">
          <div className="info-card">
            <h3>Student Information</h3>
            <div className="info-rows">
              <div className="info-row">
                <span className="label">Student ID:</span>
                <span className="value mono">{student.student_id}</span>
              </div>
              <div className="info-row">
                <span className="label">Full Name:</span>
                <span className="value">{student.first_name} {student.last_name}</span>
              </div>
              <div className="info-row">
                <span className="label">Date of Birth:</span>
                <span className="value">{student.date_of_birth || '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Gender:</span>
                <span className="value capitalize">{student.gender || '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Class:</span>
                <span className="value">{student.class_name || '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Status:</span>
                <span className={`badge ${student.status}`}>{student.status}</span>
              </div>
              <div className="info-row">
                <span className="label">Admission Date:</span>
                <span className="value">{student.admission_date || '-'}</span>
              </div>
              {student.status !== 'active' && student.status !== 'inactive' && (
                <>
                  <div className="info-row">
                    <span className="label">Exit Date:</span>
                    <span className="value">{student.exit_date || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Exit Reason:</span>
                    <span className="value">{student.exit_reason || '-'}</span>
                  </div>
                  {student.status === 'transferred' && (
                    <div className="info-row">
                      <span className="label">Transferred To:</span>
                      <span className="value">{student.transfer_destination_school || '-'}</span>
                    </div>
                  )}
                </>
              )}
              {student.portal_account && (
                <>
                  <div className="info-row">
                    <span className="label">Portal Email:</span>
                    <span className="value mono">{student.portal_account.email}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Portal Password:</span>
                    <span className="value mono">{student.portal_account.password || '*******'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label"></span>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={handleRegenerateStudentCredentials}
                      disabled={regeneratingStudent}
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      {regeneratingStudent ? 'Regenerating...' : 'Regenerate Credentials'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="info-card">
            <h3>Additional Information</h3>
            <div className="info-rows">
              <div className="info-row">
                <span className="label">Nationality:</span>
                <span className="value">{student.nationality || '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Religion:</span>
                <span className="value">{student.religion || '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Blood Group:</span>
                <span className="value">{student.blood_group || '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Medical Conditions:</span>
                <span className="value">{student.medical_conditions || 'None'}</span>
              </div>
              <div className="info-row">
                <span className="label">Address:</span>
                <span className="value">{student.address || '-'}</span>
              </div>
              {student.admission_type === 'transfer_in' && (
                <>
                  <div className="info-row">
                    <span className="label">Admission Type:</span>
                    <span className="value">Transfer in</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Previous School:</span>
                    <span className="value">{student.previous_school_name || '-'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Transfer Certificate No.:</span>
                    <span className="value">{student.transfer_certificate_number || '-'}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Enrollment History */}
        <div className="section">
          <div className="section-header">
            <h2>Enrollment History</h2>
          </div>
          {enrollments.length === 0 ? (
            <div className="empty-state-card">
              <Info size={32} />
              <p>No enrollment history recorded yet</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Term</th>
                    <th>Class</th>
                    <th>Enrolled</th>
                    <th>Ended</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map(e => (
                    <tr key={e.id}>
                      <td>{e.term_label}</td>
                      <td>{e.class_name}</td>
                      <td>{new Date(e.enrolled_at).toLocaleDateString()}</td>
                      <td>{e.ended_at ? new Date(e.ended_at).toLocaleDateString() : 'Current'}</td>
                      <td className="capitalize">{e.ended_reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Track (elective / subject-track pathway) */}
        <div className="section">
          <div className="section-header">
            <h2>Track</h2>
          </div>
          {studentTrack?.academic_term_id ? (
            <div className="info-card">
              {studentTrack.track ? (
                <div className="info-rows">
                  <div className="info-row">
                    <span className="label">Current Track:</span>
                    <span className="value">{studentTrack.track.name}</span>
                  </div>
                  <button
                    className="btn btn-sm btn-danger"
                    style={{ marginTop: 8, alignSelf: 'flex-start' }}
                    onClick={handleRemoveTrack}
                    disabled={savingTrack}
                  >
                    {savingTrack ? 'Removing...' : 'Remove from track'}
                  </button>
                </div>
              ) : (
                <p style={{ color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                  Not enrolled in a track for the current term.
                </p>
              )}
              <div className="form-row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{studentTrack.track ? 'Change to' : 'Assign to'} track</label>
                  <select value={selectedTrackToAssign} onChange={e => setSelectedTrackToAssign(e.target.value)} disabled={savingTrack}>
                    <option value="">Select track</option>
                    {availableTracks.filter(t => t.id !== studentTrack.track?.id).map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <button className="btn btn-primary" onClick={handleAssignTrack} disabled={savingTrack || !selectedTrackToAssign}>
                  {savingTrack ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <div className="empty-state-card">
              <Info size={32} />
              <p>No current academic term is set for this school, so track assignment isn't available.</p>
            </div>
          )}
        </div>

        {/* Parents Section */}
        <div className="section parents-section">
          <div className="section-header">
            <h2>Parents & Guardians</h2>
            {!showParentForm && (
              <div className="button-group">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowParentForm(true)}
                >
                  <Plus size={16} /> Add Parent
                </button>
              </div>
            )}
          </div>

          {showParentForm ? (
            <div className="form-container">
              <ParentForm
                studentId={studentId}
                onSuccess={handleParentAdded}
                onCancel={() => setShowParentForm(false)}
              />
            </div>
          ) : parents.length === 0 ? (
            <div className="empty-state-card">
              <Info size={32} />
              <p>No parents linked to this student yet</p>
            </div>
          ) : (
            <div className="parents-list">
              {parents.map(parent => (
                <div key={parent.id} className="parent-card">
                  <div className="parent-header">
                    <div>
                      <h3>{parent.first_name} {parent.last_name}</h3>
                      <span className="relationship-badge">{parent.relationship}</span>
                      {parent.is_emergency_contact && (
                        <span className="emergency-badge">Emergency Contact</span>
                      )}
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span className="label" style={{ fontSize: 12 }}>Custody: {custodyLabel(parent.custody_type)}</span>
                        <select
                          value=""
                          onChange={(e) => { if (e.target.value) handleUpdateCustodyType(parent.id, e.target.value); }}
                          disabled={custodyUpdatingParentId === parent.id}
                          style={{ fontSize: 12, padding: '2px 6px' }}
                        >
                          <option value="">
                            {custodyUpdatingParentId === parent.id ? 'Updating...' : 'Change...'}
                          </option>
                          {CUSTODY_TYPE_VALUES.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ marginTop: 6 }}>
                        {parent.is_pickup_restricted ? (
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            fontSize: 12, color: '#dc2626', background: '#fee2e2',
                            border: '1px solid #fca5a5', borderRadius: 6, padding: '3px 8px',
                          }}>
                            <Warning size={13} weight="fill" />
                            Pickup restricted{parent.restriction_reason ? `: ${parent.restriction_reason}` : ''}
                            <LockedFeature module="pickup_advanced">
                              <button
                                onClick={() => handleClearPickupRestriction(parent.id)}
                                disabled={savingRestriction}
                                style={{ marginLeft: 4, fontSize: 11, background: 'none', border: 'none', color: '#dc2626', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                              >
                                Lift
                              </button>
                            </LockedFeature>
                          </div>
                        ) : (
                          <LockedFeature module="pickup_advanced">
                            <button
                              onClick={() => openRestrictionDialog(parent)}
                              style={{ fontSize: 11, background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '2px 8px', color: '#6b7280', cursor: 'pointer' }}
                            >
                              <Warning size={12} /> Restrict Pickup (Court Order)
                            </button>
                          </LockedFeature>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleOpenLinkStudentDialog(parent.id)}
                        title="Link this parent to another child"
                      >
                        <Plus size={16} />
                        Add Sibling
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteParent(parent.id)}
                        disabled={deletingParentId === parent.id}
                        title="Remove parent"
                      >
                        <Trash size={16} />
                        {deletingParentId === parent.id ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  </div>

                  <div className="parent-details">
                    {parent.phone && (
                      <div className="detail-row">
                        <Phone size={16} className="icon" />
                        <span>{parent.phone}</span>
                      </div>
                    )}
                    {parent.email && (
                      <div className="detail-row">
                        <Envelope size={16} className="icon" />
                        <span>{parent.email}</span>
                      </div>
                    )}
                    {parent.occupation && (
                      <div className="detail-row">
                        <Briefcase size={16} className="icon" />
                        <span>{parent.occupation}</span>
                      </div>
                    )}
                    {parent.address && (
                      <div className="detail-row">
                        <MapPin size={16} className="icon" />
                        <span>{parent.address}</span>
                      </div>
                    )}
                    <>
                      <div className="detail-row">
                        <span className="label">Portal Email:</span>
                        <span className="mono">{parent.portal_account?.email || parent.email || 'Not created yet'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Portal Password:</span>
                        <span className="mono">{parent.portal_account?.password || '*******'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="icon" />
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleRegenerateParentCredentials(parent.id)}
                          disabled={regeneratingParentId === parent.id}
                          style={{ fontSize: '12px', padding: '4px 8px', marginTop: '4px' }}
                        >
                          {regeneratingParentId === parent.id ? 'Regenerating...' : 'Regenerate Credentials'}
                        </button>
                      </div>
                    </>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showLinkStudentDialog && (
            <div className="modal-overlay" onClick={() => setShowLinkStudentDialog(false)}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Add Sibling</h2>
                  <button className="modal-close" onClick={() => setShowLinkStudentDialog(false)}>
                    <X size={20} />
                  </button>
                </div>
                <div className="modal-content" style={{ padding: '20px' }}>
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Custody Type</label>
                    <select value={linkCustodyType} onChange={(e) => setLinkCustodyType(e.target.value)}>
                      {CUSTODY_TYPE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <input
                      type="text"
                      placeholder="Search by student name or ID..."
                      value={searchStudentFilter}
                      onChange={(e) => setSearchStudentFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  {availableStudents.filter(s =>
                    s.full_name.toLowerCase().includes(searchStudentFilter.toLowerCase()) ||
                    s.student_id.toLowerCase().includes(searchStudentFilter.toLowerCase())
                  ).length === 0 ? (
                    <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>
                      <p>{availableStudents.length === 0 ? 'No other students available' : 'No students match your search'}</p>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {availableStudents
                        .filter(s =>
                          s.full_name.toLowerCase().includes(searchStudentFilter.toLowerCase()) ||
                          s.student_id.toLowerCase().includes(searchStudentFilter.toLowerCase())
                        )
                        .map(student => (
                          <div
                            key={student.id}
                            style={{
                              padding: '12px',
                              marginBottom: '8px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 500 }}>{student.full_name}</div>
                              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                ID: {student.student_id} | Class: {student.class_name || '-'}
                              </div>
                            </div>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleLinkParentToStudent(student.id)}
                              disabled={linkingStudentId === student.id}
                            >
                              {linkingStudentId === student.id ? 'Linking...' : 'Link'}
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Siblings Section */}
        <div className="section">
          <div className="section-header">
            <h2>Siblings</h2>
            <button className="btn btn-primary" onClick={() => setShowAddSiblingModal(true)}>
              <Plus size={16} /> Link Sibling
            </button>
          </div>
          {siblings.length === 0 ? (
            <div className="empty-state-card">
              <Info size={32} />
              <p>No siblings linked yet</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Student ID</th>
                    <th>Class</th>
                    <th>Relationship</th>
                    <th>Source</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {siblings.map(s => (
                    <tr key={s.id || s.sibling_student_id}>
                      <td>{s.full_name}</td>
                      <td className="mono">{s.student_code}</td>
                      <td>{s.class_id || '-'}</td>
                      <td className="capitalize">{s.relationship_type}</td>
                      <td>{s.source === 'shared_parent' ? 'Shared Parent' : 'Explicit Link'}</td>
                      <td>
                        {s.source === 'explicit' && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => setConfirmRemoveSiblingId(s.id)}
                            title="Remove sibling link"
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Emergency Contacts Section */}
        <div className="section">
          <div className="section-header">
            <h2>Emergency Contacts</h2>
            <button
              className="btn btn-primary"
              onClick={() => { setEditingContact(null); setShowEmergencyContactModal(true); }}
            >
              <Plus size={16} /> Add Contact
            </button>
          </div>
          {emergencyContacts.length === 0 ? (
            <div className="empty-state-card">
              <Info size={32} />
              <p>No emergency contacts recorded yet</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Relationship</th>
                    <th>Phone</th>
                    <th>Alternate Phone</th>
                    <th>Priority</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {emergencyContacts.map(c => (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td>{c.relationship}</td>
                      <td>{c.phone}</td>
                      <td>{c.alternate_phone || '-'}</td>
                      <td>{c.priority_order}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn-icon"
                            onClick={() => { setEditingContact(c); setShowEmergencyContactModal(true); }}
                            title="Edit contact"
                          >
                            <PencilSimple size={14} />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={() => setConfirmRemoveContactId(c.id)}
                            title="Remove contact"
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Transfer Requests Section */}
        <div className="section">
          <div className="section-header">
            <h2>Transfer Requests</h2>
            <button
              className="btn btn-primary"
              onClick={() => { setEditingTransfer(null); setShowTransferRequestModal(true); }}
            >
              <Plus size={16} /> New Transfer Request
            </button>
          </div>
          {transferRequests.length === 0 ? (
            <div className="empty-state-card">
              <Info size={32} />
              <p>No transfer requests recorded yet</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Receiving School</th>
                    <th>Status</th>
                    <th>Release Date</th>
                    <th>Documents Released</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {transferRequests.map(t => (
                    <tr key={t.id}>
                      <td>{t.receiving_school_name}</td>
                      <td><span className={`badge ${t.status}`}>{t.status.replace('_', ' ')}</span></td>
                      <td>{t.release_date || '-'}</td>
                      <td>{t.documents_released || '-'}</td>
                      <td>{t.notes || '-'}</td>
                      <td>
                        <button
                          className="btn-icon"
                          onClick={() => { setEditingTransfer(t); setShowTransferRequestModal(true); }}
                          title="Update transfer request"
                        >
                          <PencilSimple size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Documents Section */}
        <div className="section">
          <div className="section-header">
            <h2>Documents</h2>
          </div>
          <DocumentsPanel ownerType="student" ownerId={studentId} canManage={true} />
        </div>

        {/* Edit Student Modal */}
        <EditStudentModal
          student={student}
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditStudent}
          onPhotoUploaded={handlePhotoUploaded}
        />

        {/* Exit Student Modal */}
        <ExitStudentModal
          student={student}
          open={showExitModal}
          onClose={() => setShowExitModal(false)}
          onExited={handleExitStudent}
        />

        {/* Re-enroll Modal */}
        <ReEnrollModal
          student={student}
          open={showReEnrollModal}
          onClose={() => setShowReEnrollModal(false)}
          onReEnrolled={handleReEnrollStudent}
        />

        {/* Add Sibling Modal */}
        <AddSiblingModal
          studentId={studentId}
          open={showAddSiblingModal}
          onClose={() => setShowAddSiblingModal(false)}
          onAdded={handleAddSibling}
          excludeIds={siblings.map(s => s.sibling_student_id)}
        />

        {/* Emergency Contact Modal */}
        <EmergencyContactModal
          open={showEmergencyContactModal}
          onClose={() => { setShowEmergencyContactModal(false); setEditingContact(null); }}
          onSave={handleSaveEmergencyContact}
          initial={editingContact}
        />

        {/* Transfer Request Modal */}
        <TransferRequestModal
          open={showTransferRequestModal}
          onClose={() => { setShowTransferRequestModal(false); setEditingTransfer(null); }}
          onSave={handleSaveTransferRequest}
          initial={editingTransfer}
        />

        <DialogModal
          isOpen={showDeactivateDialog}
          onClose={() => setShowDeactivateDialog(false)}
          title="Deactivate Student"
          message="Place this student on temporary inactive status (leave of absence, medical leave, suspension). Their enrollment stays intact — use Reactivate to bring them back."
          type="warning"
          input={{ label: 'Reason', placeholder: 'e.g. Medical leave', required: true }}
          primaryAction={{ label: 'Deactivate', onClick: (reason) => handleDeactivateStudent(reason) }}
          secondaryAction={{ label: 'Cancel' }}
        />

        <DialogModal
          isOpen={confirmReactivate}
          onClose={() => setConfirmReactivate(false)}
          title="Reactivate Student"
          message="Return this student to active status?"
          type="info"
          primaryAction={{ label: 'Reactivate', onClick: handleReactivateStudent }}
          secondaryAction={{ label: 'Cancel' }}
        />

        <DialogModal
          isOpen={Boolean(confirmDeleteParentId)}
          onClose={() => setConfirmDeleteParentId(null)}
          title="Remove Parent"
          message="Are you sure you want to remove this parent from the student?"
          type="warning"
          primaryAction={{ label: confirmLoading ? 'Removing...' : 'Remove', onClick: confirmDeleteParent }}
          secondaryAction={{ label: 'Cancel' }}
          showCloseButton={!confirmLoading}
          isLoading={confirmLoading}
          disableCloseWhenLoading={true}
        />

        <DialogModal
          isOpen={confirmRegenerateStudent}
          onClose={() => setConfirmRegenerateStudent(false)}
          title="Regenerate Student Credentials"
          message="Are you sure you want to regenerate the student's portal credentials? This will create new login information."
          type="warning"
          primaryAction={{ label: confirmLoading ? 'Regenerating...' : 'Regenerate', onClick: confirmRegenerateStudentCredentials }}
          secondaryAction={{ label: 'Cancel' }}
          showCloseButton={!confirmLoading}
          isLoading={confirmLoading}
          disableCloseWhenLoading={true}
        />

        <DialogModal
          isOpen={Boolean(confirmRegenerateParentId)}
          onClose={() => setConfirmRegenerateParentId(null)}
          title="Regenerate Parent Credentials"
          message="Are you sure you want to regenerate this parent's portal credentials? This will create new login information."
          type="warning"
          primaryAction={{ label: confirmLoading ? 'Regenerating...' : 'Regenerate', onClick: confirmRegenerateParentCredentials }}
          secondaryAction={{ label: 'Cancel' }}
          showCloseButton={!confirmLoading}
          isLoading={confirmLoading}
          disableCloseWhenLoading={true}
        />

        <DialogModal
          isOpen={Boolean(confirmRemoveSiblingId)}
          onClose={() => setConfirmRemoveSiblingId(null)}
          title="Remove Sibling Link"
          message="Are you sure you want to remove this sibling link?"
          type="warning"
          primaryAction={{ label: confirmLoading ? 'Removing...' : 'Remove', onClick: confirmRemoveSibling }}
          secondaryAction={{ label: 'Cancel' }}
          showCloseButton={!confirmLoading}
          isLoading={confirmLoading}
          disableCloseWhenLoading={true}
        />

        <DialogModal
          isOpen={Boolean(confirmRemoveContactId)}
          onClose={() => setConfirmRemoveContactId(null)}
          title="Remove Emergency Contact"
          message="Are you sure you want to remove this emergency contact?"
          type="warning"
          primaryAction={{ label: confirmLoading ? 'Removing...' : 'Remove', onClick: confirmRemoveContact }}
          secondaryAction={{ label: 'Cancel' }}
          showCloseButton={!confirmLoading}
          isLoading={confirmLoading}
          disableCloseWhenLoading={true}
        />

        <DialogModal
          isOpen={Boolean(restrictionDialog)}
          onClose={() => setRestrictionDialog(null)}
          title="Restrict Pickup Access"
          message={`This will block ${restrictionDialog?.parentName || 'this parent'} from generating or sharing a pickup QR code, or signalling "on my way" — enforced immediately, and should only be set against actual court documentation.`}
          type="warning"
          input={{ label: 'Reason (e.g. court order reference)', placeholder: 'Case #, date, and restriction details', defaultValue: restrictionDialog?.reason || '', required: true }}
          primaryAction={{ label: savingRestriction ? 'Saving...' : 'Restrict Pickup', onClick: handleSetPickupRestriction }}
          secondaryAction={{ label: 'Cancel' }}
          showCloseButton={!savingRestriction}
          isLoading={savingRestriction}
          disableCloseWhenLoading={true}
        />
      </div>
    </Layout>
  );
}

export default StudentDetailPage;
